/**
 * ManaTiming - Main Entry Point
 * Simplified version without access control for volunteer ease-of-use
 */

import './style.css'
import { initTiming, startRace, stopRace, recordFinish, undoLastFinish, removeTimestamp, insertTimestamp, getTimestamps, formatTime, parseTime, updateTimestampTime, addManualTimestamp, isRaceActive, isRaceFinished, getElapsedTime, mergeRemoteTimestamps, setMasterTimerLock, isMasterTimer, getMasterDeviceId, deviceId } from './lib/timing.js'
import { initFirebase, getConnectionStatus, subscribeToTimestamps, subscribeToBibEntries, claimTimerLock, releaseTimerLock, subscribeToTimerLock, clearAllFirebaseData, fetchRacesFromCloud, fetchAthletesFromCloud, fetchAllAthletesFromCloud, subscribeToAllRaces, subscribeToAthletes } from './lib/firebase.js'
import { renderRosterPage } from './pages/roster.js'
import { renderChutePage, initChutePage } from './pages/chute.js'
import { renderResultsPage } from './pages/results.js'
import { renderRaceManagerPage } from './pages/race-manager.js'
import { renderLiveResultsPage } from './pages/live-results.js'
import { getSelectedRace, saveLocal, getRaceKey, STORAGE_KEYS, getRaces, saveRaces, saveRoster, setSelectedRace, setCloudRaces, setCloudAthletes } from './lib/storage.js'
import { checkAutoSeed, seedTestData, seedFootForwardRaces } from './lib/seed-data.js'
import { showConfirm, showPrompt } from './lib/ui.js'
import { getCurrentRole, isStaffDomain } from './lib/access.js'

// Global error handler
window.onerror = function (msg, url, line, col, error) {
  document.getElementById('app').innerHTML = `
    <div style="padding:20px;color:#FF4444;background:#1a1a1a;font-family:monospace;">
      <h2>Error Loading App</h2>
      <p>${msg}</p>
      <p>At: ${url}:${line}:${col}</p>
      <pre>${error?.stack || 'No stack trace'}</pre>
    </div>
  `
  return false
}

// Initialize
const timing = initTiming()
const firebase = initFirebase()

/**
 * Sync races from Firebase to local storage
 * Merges cloud races with local races (cloud wins for conflicts)
 */
async function syncRacesFromCloud() {
  try {
    const cloudRaces = await fetchRacesFromCloud()
    if (cloudRaces.length === 0) return

    const localRaces = getRaces()
    const localRaceIds = new Set(localRaces.map(r => r.id))

    // Merge: add cloud races that don't exist locally
    let added = 0
    for (const cloudRace of cloudRaces) {
      if (!localRaceIds.has(cloudRace.id)) {
        localRaces.push(cloudRace)
        added++

        // Also fetch athletes for this race
        const athletes = await fetchAthletesFromCloud(cloudRace.id)
        if (athletes.length > 0) {
          saveRoster(athletes, cloudRace.id)
          console.log(`Synced ${athletes.length} athletes for race: ${cloudRace.name}`)
        }
      }
    }

    if (added > 0) {
      saveRaces(localRaces)
      console.log(`Synced ${added} new races from cloud`)
      // Re-render to show new races
      render()
    }
  } catch (err) {
    console.error('Error syncing races from cloud:', err)
  }
}

/**
 * Reset local data and sync everything fresh from Firebase
 * Use this to set up a new device or fix sync issues
 */
async function resetAndSyncFromCloud() {
  console.log('Resetting local data and syncing from cloud...')

  // Clear all local storage keys
  const keysToRemove = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key && key.startsWith('manatiming_')) {
      keysToRemove.push(key)
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key))
  console.log(`Cleared ${keysToRemove.length} local storage keys`)

  // Fetch races from cloud
  const cloudRaces = await fetchRacesFromCloud()
  if (cloudRaces.length > 0) {
    saveRaces(cloudRaces)
    console.log(`Loaded ${cloudRaces.length} races from cloud`)

    // Fetch athletes for each race
    for (const race of cloudRaces) {
      const athletes = await fetchAthletesFromCloud(race.id)
      if (athletes.length > 0) {
        saveRoster(athletes, race.id)
        console.log(`Loaded ${athletes.length} athletes for ${race.name}`)
      }
    }

    // Select first race by default
    if (cloudRaces.length > 0) {
      setSelectedRace(cloudRaces[0].id)
    }
  }

  // Reload the page to reinitialize everything
  window.location.reload()
}

// Note: Races are now synced in real-time via subscribeToAllRaces
// The old syncRacesFromCloud is kept for manual refresh if needed

// Check for auto-seed parameter (?seed=test)
const seededData = checkAutoSeed()
if (seededData) {
  console.log('Auto-seeded test data for volunteer training')
}

// Make seedTestData available globally for console use
window.seedTestData = seedTestData

// Make seedFootForwardRaces available globally for race setup
window.seedFootForwardRaces = seedFootForwardRaces

// Make clearAllFirebaseData available globally for admin cleanup
window.clearAllFirebaseData = clearAllFirebaseData

// Make syncRacesFromCloud available globally for manual sync
window.syncRacesFromCloud = syncRacesFromCloud

// Make resetAndSyncFromCloud available globally for device setup
window.resetAndSyncFromCloud = resetAndSyncFromCloud

// Current page state
let currentPage = 'tapper'

// State
let elapsedInterval = null

// Firebase subscriptions
let timestampUnsubscribe = null
let bibEntriesUnsubscribe = null
let timerLockUnsubscribe = null
let athletesUnsubscribe = null
let racesUnsubscribe = null
let subscribedRaceId = null

/**
 * Set up global races subscription (cloud-first)
 * This keeps the race list synced across all devices in real-time
 */
function setupRacesSubscription() {
  if (racesUnsubscribe) return // Already subscribed

  console.log('Setting up races subscription (cloud-first mode)')
  racesUnsubscribe = subscribeToAllRaces((races) => {
    console.log('Cloud races updated:', races.length)
    setCloudRaces(races)
    // Re-render if on races page
    if (currentPage === 'races') {
      render()
    }
  })
}

/**
 * Preload all athletes from all races
 * Enables cross-race bib lookup (for runners in multiple events)
 */
async function preloadAllAthletes() {
  console.log('Preloading all athletes for cross-race lookup...')
  const athletesByRace = await fetchAllAthletesFromCloud()
  for (const raceId in athletesByRace) {
    setCloudAthletes(raceId, athletesByRace[raceId])
  }
  console.log('Athletes preloaded for', Object.keys(athletesByRace).length, 'races')
}

// Set up races subscription immediately after Firebase init
setupRacesSubscription()

// Preload all athletes for cross-race lookup
preloadAllAthletes()

/**
 * Set up Firebase real-time subscriptions for the selected race
 */
function setupFirebaseSubscriptions() {
  const selectedRace = getSelectedRace()
  if (!selectedRace?.id) {
    console.log('No race selected, skipping Firebase subscriptions')
    cleanupFirebaseSubscriptions()
    return
  }

  // If race changed, clean up old subscriptions
  if (subscribedRaceId && subscribedRaceId !== selectedRace.id) {
    console.log('Race changed, cleaning up old subscriptions')
    cleanupFirebaseSubscriptions()
  }

  subscribedRaceId = selectedRace.id

  // Subscribe to timestamps
  if (!timestampUnsubscribe) {
    console.log('Setting up timestamp subscription for race:', selectedRace.id)
    timestampUnsubscribe = subscribeToTimestamps(selectedRace.id, (remoteTimestamps) => {
      console.log('Received remote timestamps:', remoteTimestamps.length)
      // Merge with local timestamps
      mergeRemoteTimestamps(remoteTimestamps)
      // Re-render if on tapper or results page
      if (currentPage === 'tapper' || currentPage === 'results') {
        render()
      }
    })
  }

  // Subscribe to bib entries (for results page and cross-device visibility)
  if (!bibEntriesUnsubscribe) {
    console.log('Setting up bib entries subscription for race:', selectedRace.id)
    bibEntriesUnsubscribe = subscribeToBibEntries(selectedRace.id, (remoteBibEntries) => {
      console.log('Received remote bib entries:', remoteBibEntries.length)
      // Store in localStorage for the chute/results pages to use
      saveLocal(getRaceKey(STORAGE_KEYS.BIBS, selectedRace.id), remoteBibEntries)
      // Re-render if on chute or results page
      if (currentPage === 'chute' || currentPage === 'results') {
        render()
      }
    })
  }

  // Subscribe to athletes for this race (cloud-first roster)
  if (!athletesUnsubscribe) {
    console.log('Setting up athletes subscription for race:', selectedRace.id)
    athletesUnsubscribe = subscribeToAthletes(selectedRace.id, (athletes) => {
      console.log('Received athletes for race:', athletes.length)
      setCloudAthletes(selectedRace.id, athletes)
      // Re-render if on results, chute, or roster page
      if (currentPage === 'results' || currentPage === 'chute' || currentPage === 'roster' || currentPage === 'live') {
        render()
      }
    })
  }

  // Subscribe to timer lock
  if (!timerLockUnsubscribe) {
    console.log('Setting up timer lock subscription for race:', selectedRace.id)
    timerLockUnsubscribe = subscribeToTimerLock(selectedRace.id, (lock) => {
      console.log('Received timer lock update:', lock)
      setMasterTimerLock(lock)
      if (currentPage === 'tapper') {
        renderTapperUI() // Partial re-render specifically for tapper
      }
    })
  }
}

/**
 * Clean up Firebase subscriptions
 */
function cleanupFirebaseSubscriptions() {
  if (timestampUnsubscribe) {
    timestampUnsubscribe()
    timestampUnsubscribe = null
  }
  if (bibEntriesUnsubscribe) {
    bibEntriesUnsubscribe()
    bibEntriesUnsubscribe = null
  }
  if (athletesUnsubscribe) {
    athletesUnsubscribe()
    athletesUnsubscribe = null
  }
  if (timerLockUnsubscribe) {
    timerLockUnsubscribe()
    timerLockUnsubscribe = null
  }
  subscribedRaceId = null
}

// Configurable hotkey for finish (stored in localStorage)
const DEFAULT_FINISH_KEY = ' ' // Spacebar
let finishKey = localStorage.getItem('manatiming_finish_key') || DEFAULT_FINISH_KEY

/**
 * Set up global keyboard handler
 */
function setupKeyboardHandler() {
  document.addEventListener('keydown', (e) => {
    // Ignore if typing in an input field
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
      return
    }

    // Finish key (default: spacebar)
    if (e.key === finishKey && isRaceActive()) {
      e.preventDefault()
      handleFinish()
      return
    }

    // Enter to start race (when not started)
    if (e.key === 'Enter' && !isRaceActive()) {
      e.preventDefault()
      handleStartRace()
      return
    }

    // Ctrl+Z or Cmd+Z for undo
    if ((e.ctrlKey || e.metaKey) && e.key === 'z' && isRaceActive()) {
      e.preventDefault()
      handleUndo()
      return
    }
  })
}

/**
 * Update the finish hotkey
 */
function setFinishKey(key) {
  finishKey = key
  localStorage.setItem('manatiming_finish_key', key)
}

/**
 * Get display name for a key
 */
function getKeyDisplayName(key) {
  const keyNames = {
    ' ': 'Space',
    'Enter': 'Enter',
    'Tab': 'Tab',
    'ArrowUp': '↑',
    'ArrowDown': '↓',
    'ArrowLeft': '←',
    'ArrowRight': '→'
  }
  return keyNames[key] || key.toUpperCase()
}

// Make setFinishKey available globally for settings page
window.setFinishKey = setFinishKey

/**
 * Show a toast notification
 */
function showToast(message, type = 'info') {
  // Get or create toast container
  let container = document.querySelector('.toast-container')
  if (!container) {
    container = document.createElement('div')
    container.className = 'toast-container'
    document.body.appendChild(container)
  }

  const toast = document.createElement('div')
  toast.className = `toast ${type}`
  toast.textContent = message

  container.appendChild(toast)

  // Remove after animation completes
  setTimeout(() => {
    toast.remove()
    // Remove container if empty
    if (container.children.length === 0) {
      container.remove()
    }
  }, 3000)
}

// Make showToast globally available
window.showToast = showToast

/**
 * Render the Tapper UI
 */
function renderTapperUI() {
  const app = document.getElementById('app')
  const timestamps = getTimestamps()
  const raceActive = isRaceActive()
  const selectedRace = getSelectedRace()

  app.innerHTML = `
    <header class="header">
      <div class="header-content">
        <h1 class="logo">Mana<span class="text-accent">Timing</span></h1>
        <div class="sync-status">
          <span class="sync-dot ${getConnectionStatus() === 'connected' ? '' : 'offline'}"></span>
          <span>${getConnectionStatus()}</span>
        </div>
      </div>
      ${selectedRace ? `
        <div class="selected-race-info">
          <span class="race-name">${selectedRace.name}</span>
          ${selectedRace.distanceValue ? `<span class="race-distance">${selectedRace.distanceValue} ${selectedRace.distanceUnit === 'kilometers' ? 'km' : 'mi'}</span>` : ''}
          <span class="race-id" style="font-size:0.5rem;color:#666;">${selectedRace.id.slice(0, 8)}</span>
        </div>
      ` : ''}
      ${raceActive ? `<div class="elapsed-time" id="elapsed">${formatTime(getElapsedTime())}</div>` : ''}
    </header>

    <main class="main-content">
      <div class="tap-area">
        ${!isMasterTimer() ? `
          <div class="timer-lock-status">
            <div class="lock-info">
              <span class="lock-label">Timer Active on Device</span>
              <span class="lock-device">${getMasterDeviceId().slice(0, 8)}...</span>
            </div>
            <button class="btn-take-over" id="takeOverBtn">Take Over</button>
          </div>
        ` : ''}

        ${isRaceFinished() ? `
          <div class="race-finished-msg">
            <h2>Race Finished</h2>
            <p>Ready for results & scoring</p>
            <button class="btn-secondary" id="resetRaceBtn">Reset / New Race</button>
          </div>
        ` : !raceActive ? `
          ${!selectedRace ? `
            <div class="no-race-selected">
              <p>No race selected</p>
              <button class="btn-select-race" id="selectRaceBtn">Select a Race</button>
              <p class="text-secondary">or start timing without a race:</p>
            </div>
          ` : ''}
          <button class="btn-start" id="startBtn">
            START RACE
          </button>
        ` : `
          <div class="flash-overlay"></div>
          <button class="btn-tap" id="finishBtn" ${!isMasterTimer() ? 'disabled' : ''}>
            FINISH
          </button>

          <div class="tap-stats">
            <div class="tap-count-large">${timestamps.length}</div>
            <div class="tap-count-label">recorded</div>
            ${timestamps.length > 0 ? `
              <div class="last-time">Last: ${formatTime(timestamps[timestamps.length - 1].netTime)}</div>
            ` : ''}
          </div>

          <div class="tap-actions">
            ${timestamps.length > 0 ? `<button class="btn-undo-inline" id="undoBtn">Undo Last</button>` : ''}
            <button class="btn-stop-race" id="stopRaceBtn">STOP RACE</button>
          </div>
        `}
      </div>
    </main>

    ${renderNavBar()}
  `

  // Attach event handlers
  attachEventHandlers()
}

/**
 * Navigate to a page
 */
function navigateTo(page) {
  currentPage = page
  window.location.hash = page
  render()
}

/**
 * Main render function - routes to correct page
 */
function render() {
  const app = document.getElementById('app')

  // Refresh timing state for the selected race
  initTiming()

  const role = getCurrentRole()
  const staffDomain = isStaffDomain()

  // Routing security: If not on staff domain, force to results/live if trying to access staff pages
  if (!staffDomain && !['results', 'live'].includes(currentPage)) {
    console.log('Redirecting to results for public domain access')
    currentPage = 'results'
    window.location.hash = 'results'
  }

  // Set up Firebase subscriptions if we have a race selected
  setupFirebaseSubscriptions()

  switch (currentPage) {
    case 'roster':
      app.innerHTML = `
        <div class="page-container">
          <main class="main-content" id="pageContent"></main>
          ${renderNavBar()}
        </div>
      `
      renderRosterPage(document.getElementById('pageContent'), navigateTo)
      break

    case 'chute':
      // If we are navigating TO chute, reset state. If re-rendering, don't.
      // But here we rely on the fact that navigateTo calls render().
      // This switch is inside render().
      // We need to know if this is a fresh navigation or a re-render.
      // For now, we rely on the fact that we moved reset logic to initChutePage
      // and renderChutePage only loads data.
      // So we must manually call initChutePage when switching TO 'chute'.
      // This is handled in navigateTo.
      app.innerHTML = `
        <div class="page-container">
          <main class="main-content" id="pageContent"></main>
          ${renderNavBar()}
        </div>
      `
      renderChutePage(document.getElementById('pageContent'), navigateTo)
      break

    case 'results':
      app.innerHTML = `
        <div class="page-container">
          <main class="main-content" id="pageContent"></main>
          ${renderNavBar()}
        </div>
      `
      renderResultsPage(document.getElementById('pageContent'), navigateTo)
      break

    case 'live':
      app.innerHTML = `
        <div class="page-container">
          <main class="main-content" id="pageContent"></main>
          ${renderNavBar()}
        </div>
      `
      renderLiveResultsPage(document.getElementById('pageContent'), navigateTo)
      break

    case 'races':
      app.innerHTML = `
        <div class="page-container">
          <main class="main-content" id="pageContent"></main>
          ${renderNavBar()}
        </div>
      `
      renderRaceManagerPage(document.getElementById('pageContent'), navigateTo)
      break

    case 'settings':
      renderSettingsPage(app)
      break

    case 'tapper':
    default:
      renderTapperUI()
      break
  }

  attachNavHandlers()
}

/**
 * Render the navigation bar
 */
function renderNavBar() {
  const role = getCurrentRole()
  const staffDomain = isStaffDomain()

  return `
    <nav class="nav-bar">
      ${staffDomain ? `
        <a href="#tapper" class="nav-item ${currentPage === 'tapper' ? 'active' : ''}" data-page="tapper">
          <span class="nav-icon">⏱</span>
          <span>Timer</span>
        </a>
        <a href="#chute" class="nav-item ${currentPage === 'chute' ? 'active' : ''}" data-page="chute">
          <span class="nav-icon">📋</span>
          <span>Chute</span>
        </a>
      ` : ''}
      <a href="#results" class="nav-item ${currentPage === 'results' ? 'active' : ''}" data-page="results">
        <span class="nav-icon">🏆</span>
        <span>Results</span>
      </a>
      ${staffDomain ? `
        <a href="#races" class="nav-item ${currentPage === 'races' ? 'active' : ''}" data-page="races">
          <span class="nav-icon">🏁</span>
          <span>Races</span>
        </a>
        <a href="#roster" class="nav-item ${currentPage === 'roster' ? 'active' : ''}" data-page="roster">
          <span class="nav-icon">👥</span>
          <span>Roster</span>
        </a>
      ` : ''}
    </nav>
  `
}

/**
 * Render settings page
 */
function renderSettingsPage(app) {
  app.innerHTML = `
    <div class="page-container">
      <main class="main-content settings-page">
        <header class="page-header">
          <h1>Settings</h1>
        </header>
        <div class="settings-section">
          <h3>Finish Hotkey</h3>
          <p class="text-secondary">Current: <kbd>${getKeyDisplayName(finishKey)}</kbd></p>
          <button class="btn-secondary" id="changeHotkeyBtn">Change Hotkey</button>
        </div>
        <div class="settings-section">
          <h3>Connection Status</h3>
          <p><span class="sync-dot ${getConnectionStatus() === 'connected' ? '' : 'offline'}"></span> ${getConnectionStatus()}</p>
        </div>
      </main>
      ${renderNavBar()}
    </div>
  `
  attachNavHandlers()

  document.getElementById('changeHotkeyBtn')?.addEventListener('click', () => {
    const listener = (e) => {
      e.preventDefault()
      setFinishKey(e.key)
      document.removeEventListener('keydown', listener)
      render()
    }
    alert('Press any key to set as the new finish hotkey...')
    document.addEventListener('keydown', listener, { once: true })
  })
}

/**
 * Attach navigation handlers
 */
function attachNavHandlers() {
  document.querySelectorAll('.nav-item').forEach(item => {
    item.addEventListener('click', (e) => {
      e.preventDefault()
      const page = item.dataset.page
      if (page) {
        if (page === 'chute') {
          initChutePage()
        }
        navigateTo(page)
      }
    })
  })
}

/**
 * Attach event handlers
 */
function attachEventHandlers() {
  const startBtn = document.getElementById('startBtn')
  const finishBtn = document.getElementById('finishBtn')
  const undoBtn = document.getElementById('undoBtn')

  if (startBtn) {
    startBtn.addEventListener('click', handleStartRace)
  }

  const selectRaceBtn = document.getElementById('selectRaceBtn')
  if (selectRaceBtn) {
    selectRaceBtn.addEventListener('click', () => {
      navigateTo('races')
    })
  }

  if (finishBtn) {
    finishBtn.addEventListener('click', handleFinish)
    // Also handle touch for faster response on mobile
    finishBtn.addEventListener('touchstart', (e) => {
      e.preventDefault()
      handleFinish()
    })
  }

  const stopRaceBtn = document.getElementById('stopRaceBtn')
  if (stopRaceBtn) {
    stopRaceBtn.addEventListener('click', async () => {
      const confirmed = await showConfirm({
        title: 'Finish Race?',
        message: 'This will stop the timer and disable further finish recording.',
        confirmText: 'Finish Race',
        cancelText: 'Keep Timing',
        type: 'warning'
      })
      if (confirmed) {
        handleStopRace()
      }
    })
  }

  const resetRaceBtn = document.getElementById('resetRaceBtn')
  if (resetRaceBtn) {
    resetRaceBtn.addEventListener('click', async () => {
      const confirmed = await showConfirm({
        title: 'Reset Race?',
        message: 'All timestamps and bib entries will be cleared. This cannot be undone.',
        confirmText: 'Reset Everything',
        cancelText: 'Cancel',
        type: 'danger'
      })
      if (confirmed) {
        handleResetRace()
      }
    })
  }

  if (undoBtn) {
    undoBtn.addEventListener('click', handleUndo)
  }

  // Remove buttons
  document.querySelectorAll('.btn-remove').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation()
      const id = btn.dataset.id
      const confirmed = await showConfirm({
        title: 'Remove Timestamp?',
        message: 'This will remove the timestamp and re-sequence all entries after it.',
        confirmText: 'Remove',
        type: 'danger'
      })
      if (confirmed) {
        handleRemove(id)
      }
    })
  })

  // Insert buttons (after specific position)
  document.querySelectorAll('.btn-insert').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation()
      const afterSequence = parseInt(btn.dataset.after, 10)
      handleInsert(afterSequence)
    })
  })

  // Insert at first position
  const insertFirstBtn = document.querySelector('.btn-insert-first')
  if (insertFirstBtn) {
    insertFirstBtn.addEventListener('click', (e) => {
      e.stopPropagation()
      handleInsert(0)
    })
  }

  // Add manual time button
  const addTimeBtn = document.getElementById('addTimeBtn')
  if (addTimeBtn) {
    addTimeBtn.addEventListener('click', async () => {
      const timeStr = await showPrompt({
        title: 'Add Manual Time',
        placeholder: 'MM:SS.ss or MM:SS',
        inputType: 'text'
      })
      if (timeStr) {
        handleAddManualTime(timeStr)
      }
    })
  }

  // Edit time buttons
  document.querySelectorAll('.btn-edit-time').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation()
      const id = btn.dataset.id
      const timestamps = getTimestamps()
      const timestamp = timestamps.find(t => t.id === id)
      if (timestamp) {
        const currentTime = formatTime(timestamp.netTime)
        const newTimeStr = await showPrompt({
          title: 'Edit Time',
          placeholder: 'MM:SS.ss',
          defaultValue: currentTime
        })
        if (newTimeStr && newTimeStr !== currentTime) {
          handleEditTime(id, newTimeStr)
        }
      }
    })
  })

  // Clickable times for editing
  document.querySelectorAll('.time.editable').forEach(el => {
    el.addEventListener('click', async (e) => {
      e.stopPropagation()
      const id = el.dataset.id
      const timestamps = getTimestamps()
      const timestamp = timestamps.find(t => t.id === id)
      if (timestamp) {
        const currentTime = formatTime(timestamp.netTime)
        const newTimeStr = await showPrompt({
          title: 'Edit Time',
          placeholder: 'MM:SS.ss',
          defaultValue: currentTime
        })
        if (newTimeStr && newTimeStr !== currentTime) {
          handleEditTime(id, newTimeStr)
        }
      }
    })
  })

  const takeOverBtn = document.getElementById('takeOverBtn')
  if (takeOverBtn) {
    takeOverBtn.addEventListener('click', async () => {
      const confirmed = await showConfirm({
        title: 'Take Over Timer?',
        message: 'This will take control of the timer from the other device.',
        confirmText: 'Take Over',
        type: 'warning'
      })
      if (confirmed) {
        const selectedRace = getSelectedRace()
        if (selectedRace?.id) {
          claimTimerLock(selectedRace.id, deviceId)
          renderTapperUI() // Re-render to update UI
        }
      }
    })
  }
}

/**
 * Handle race start
 */
function handleStartRace() {
  const selectedRace = getSelectedRace()
  if (selectedRace?.id) {
    claimTimerLock(selectedRace.id, deviceId)
  }
  startRace()
  render()
  startElapsedTimer()
}

/**
 * Handle finish tap
 */
function handleFinish() {
  // If no master exists yet, claim it on first tap
  if (isMasterTimer() && !getMasterDeviceId()) {
    const selectedRace = getSelectedRace()
    if (selectedRace?.id) {
      claimTimerLock(selectedRace.id, deviceId)
    }
  }

  recordFinish()
  renderTapperUI()

  // Visual Flash Feedback
  const tapArea = document.querySelector('.tap-area')
  if (tapArea) {
    tapArea.classList.add('flash-active')
    setTimeout(() => tapArea.classList.remove('flash-active'), 400)
  }

  // Haptic feedback if supported
  if ('vibrate' in navigator) {
    navigator.vibrate(50)
  }
}

/**
 * Handle stop race
 */
function handleStopRace() {
  stopRace()
  // Release master timer lock when race is stopped
  const selectedRace = getSelectedRace()
  if (selectedRace?.id) {
    releaseTimerLock(selectedRace.id)
  }
  if (elapsedInterval) clearInterval(elapsedInterval)
  renderTapperUI()
}


/**
 * Handle reset race
 */
function handleResetRace() {
  // Release timer lock if any
  const selectedRace = getSelectedRace()
  if (selectedRace?.id) {
    releaseTimerLock(selectedRace.id)
  }
  // Clear session keys
  localStorage.removeItem('manatiming_race')
  localStorage.removeItem('manatiming_timestamps')
  localStorage.removeItem('manatiming_bib_entries')
  window.location.reload()
}

/**
 * Handle undo
 */
function handleUndo() {
  undoLastFinish()
  renderTapperUI()
}

/**
 * Handle remove specific timestamp
 */
function handleRemove(id) {
  removeTimestamp(id)
  renderTapperUI()
}

/**
 * Handle insert timestamp
 */
function handleInsert(afterSequence) {
  insertTimestamp(afterSequence)
  renderTapperUI()
}

/**
 * Handle edit time
 */
function handleEditTime(id, timeStr) {
  const ms = parseTime(timeStr)
  if (ms === null) {
    alert('Invalid time format. Use MM:SS.ss or MM:SS (e.g., 18:23.45)')
    return
  }
  updateTimestampTime(id, ms)
  renderTapperUI()
}

/**
 * Handle add manual time
 */
function handleAddManualTime(timeStr) {
  const ms = parseTime(timeStr)
  if (ms === null) {
    alert('Invalid time format. Use MM:SS.ss or MM:SS (e.g., 18:23.45)')
    return
  }
  addManualTimestamp(ms)
  renderTapperUI()
}

/**
 * Start elapsed time display
 */
function startElapsedTimer() {
  if (elapsedInterval) clearInterval(elapsedInterval)

  elapsedInterval = setInterval(() => {
    const elapsedEl = document.getElementById('elapsed')
    if (elapsedEl) {
      elapsedEl.textContent = formatTime(getElapsedTime())
    }
  }, 100)
}

// Handle hash-based routing
function handleHashChange() {
  const hash = window.location.hash.slice(1) || 'tapper'
  if (['races', 'tapper', 'chute', 'roster', 'results', 'live', 'settings'].includes(hash)) {
    currentPage = hash
    render()
  }
}

// Listen for hash changes
window.addEventListener('hashchange', handleHashChange)

// Initial render based on current hash
handleHashChange()

// Set up keyboard shortcuts
setupKeyboardHandler()

// Resume elapsed timer if race was already started
if (isRaceActive()) {
  startElapsedTimer()
}

console.log('ManaTiming initialized', { deviceId: timing.deviceId, finishKey: getKeyDisplayName(finishKey) })
